/**
 * Feature access configuration.
 *
 * Advanced approval functionality is restricted to the organisations listed below.
 * Organisations NOT on this list will not see:
 *   - Custom workflows (including the option to switch to them)
 *   - Approval groups
 *   - Delegation & substitution (approval routing)
 *   - Organisation Settings (base currency and timezone)
 * For those organisations, the standard "Authorisation Settings" appear on the
 * User Management page instead of the Workflows page.
 *
 * Add an organisation's UUID to ADVANCED_FEATURES_ORG_UUIDS to enable these
 * features. This is the organisation UUID shown next to the header dropdown
 * (the organisation record's `organisationId` field), not the integer id.
 * UUIDs can also be provided via the REACT_APP_ADVANCED_FEATURES_ORG_UUIDS env
 * var (comma-separated), which is merged with the list below.
 */

const parseEnvUuids = (value) =>
    (value || '')
        .split(',')
        .map((uuid) => uuid.trim())
        .filter(Boolean);

export const ADVANCED_FEATURES_ORG_UUIDS = [
    // Add organisation UUIDs here, e.g. '3f2504e0-4f89-41d3-9a0c-0305e82c3301'
    'a4abda24-30dc-4266-a891-6f5936156035', // Dev test org 4 (for staging testing)
    'b7a9a602-6b76-430c-a479-56b25b698597', // Yavrio BACS Demo Organisation
    'a7d086f8-160a-4b3e-8167-f1cb70c148c3', // Yavrio Dev BACS Organisation
    '0dc33e31-1da9-4ff2-9164-d28ef1d22f1a', // Squid Staging Org
    '999228c8-1438-489c-8018-a6078465c468', // Ficofi
    '8769face-30bc-462d-bc28-e5d47667404a', // Felix / Fareshare
    'b19c5f3e-a120-43cb-a6aa-9411cb587a05', // Xcel Bespoke Limited
    'da91e6a0-a600-4153-9b83-131e370a2acd', // Xcel Concepts Limited
    '143192b3-e30a-4f9d-8abc-7faf32d79db3', // Sunsynk Group Limited
    '96baa043-c652-4457-b82d-75eec33b021e', // Sunsynk UK Limited
    '6124985a-ec8c-439b-a5b5-1705cff4edaf', // Sureserve Test 2
    '2fce1f6d-6fc3-4bba-8b92-10130feaa8ca', // Sureserve Test
    '90876255-8b3b-4a3e-9acc-46582fb9651a', // School for Social Entrepreneurs
    '2846917b-c6b4-44cc-947f-f7af5ad0e3ca'
];

const allAdvancedFeaturesOrgUuids = [
    ...ADVANCED_FEATURES_ORG_UUIDS,
    ...parseEnvUuids(process.env.REACT_APP_ADVANCED_FEATURES_ORG_UUIDS),
];

/**
 * Whether the given organisation has access to advanced approval functionality.
 * @param {string|null|undefined} organisationUuid
 * @returns {boolean}
 */
export const hasAdvancedFeatures = (organisationUuid) => {
    if (!organisationUuid) {
        return false;
    }
    return allAdvancedFeaturesOrgUuids.some(
        (uuid) => String(uuid) === String(organisationUuid)
    );
};
